import { useMemo, useState } from 'react';
import type { CeoDebtScheduleItem } from '@shared/api';
import { Button } from '@shared/ui/Button';
import { formatDateRu, formatMoney } from '../ceoFormat';
import { ScheduleStatus } from './ScheduleStatus';
import { hasPaymentMark, isRowPaid, rowAmounts, scheduleTotals } from './scheduleModel';
import {
  ActionButtonsRow,
  CreditorContractText,
  DueSubtext,
  HeaderRow,
  InstrumentTitleText,
  RatesSubtext,
  Section,
  Table,
  TableWrap,
  Title,
} from './styles';

/** Горизонт списка «К оплате», дней. */
const DUE_HORIZON_DAYS = 90;

interface UpcomingScheduleTableProps {
  schedule: CeoDebtScheduleItem[];
  can_write: boolean;
  onSelectInstrument: (id: string) => void;
  onMarkPaid: (item: CeoDebtScheduleItem) => void;
  onUnmarkPaid: (item: CeoDebtScheduleItem) => void;
}

export function UpcomingScheduleTable({
  schedule,
  can_write,
  onSelectInstrument,
  onMarkPaid,
  onUnmarkPaid,
}: UpcomingScheduleTableProps) {
  const [showAll, setShowAll] = useState(false);
  // По умолчанию — неоплаченные: просроченные и на ближайшие 90 дней.
  const due = useMemo(
    () => schedule.filter((i) => !isRowPaid(i) && i.days_until_due <= DUE_HORIZON_DAYS),
    [schedule],
  );
  const rows = showAll ? schedule : due;
  const totals = useMemo(() => scheduleTotals(rows), [rows]);
  return (
    <Section>
      <HeaderRow>
        <Title>
          {showAll
            ? 'Пошаговый платёжный календарь по кредитам и лизингам'
            : `К оплате: просроченные и на ${DUE_HORIZON_DAYS} дней`}
        </Title>
        {schedule.length > due.length && (
          <Button $size="sm" $variant="ghost" type="button" onClick={() => setShowAll((v) => !v)}>
            {showAll ? 'Только к оплате' : `Весь график (${schedule.length})`}
          </Button>
        )}
      </HeaderRow>
      {rows.length === 0 && (
        <RatesSubtext>
          {showAll
            ? 'Графиков платежей нет.'
            : `Платежей к оплате на ${DUE_HORIZON_DAYS} дней нет.`}
        </RatesSubtext>
      )}
      <TableWrap>
        <Table>
          <thead>
            <tr>
              <th>Дата платежа</th>
              <th>Договор / Кредитор</th>
              <th>Транш</th>
              <th>Основной долг</th>
              <th>Проценты / Лизинг</th>
              <th>Комиссия</th>
              <th>По графику</th>
              <th>Оплачено</th>
              <th>Осталось</th>
              <th>Статус</th>
              <th>Действие</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((item) => {
              const isPaid = isRowPaid(item);
              const amounts = rowAmounts(item);
              return (
                <tr key={item.id}>
                  <td>
                    <strong>{formatDateRu(item.due_date)}</strong>
                    {!isPaid && (
                      <DueSubtext>
                        {item.days_until_due < 0
                          ? `просрочено ${Math.abs(item.days_until_due)} дн.`
                          : `через ${item.days_until_due} дн.`}
                      </DueSubtext>
                    )}
                  </td>
                  <td>
                    <CreditorContractText>
                      {item.creditor_name} · № {item.contract_number}
                    </CreditorContractText>
                    <InstrumentTitleText>{item.instrument_title}</InstrumentTitleText>
                  </td>
                  <td>#{item.installment_no}</td>
                  <td className="money">{formatMoney(item.principal_amount)}</td>
                  <td className="money">{formatMoney(item.interest_amount)}</td>
                  <td className="money">
                    {item.fee_amount > 0 ? formatMoney(item.fee_amount) : '—'}
                  </td>
                  <td className="money">{formatMoney(amounts.scheduled)}</td>
                  <td className="money">{amounts.paid > 0 ? formatMoney(amounts.paid) : '—'}</td>
                  <td className="money">
                    <strong>{formatMoney(amounts.left)}</strong>
                  </td>
                  <td>
                    <ScheduleStatus item={item} />
                  </td>
                  <td>
                    <ActionButtonsRow>
                      <Button
                        $size="sm"
                        $variant="ghost"
                        onClick={() => onSelectInstrument(item.instrument_id)}
                      >
                        Карточка
                      </Button>
                      {can_write && !isPaid && (
                        <Button $size="sm" $variant="secondary" onClick={() => onMarkPaid(item)}>
                          Отметить оплату
                        </Button>
                      )}
                      {can_write && hasPaymentMark(item) && (
                        <Button $size="sm" $variant="ghost" onClick={() => onUnmarkPaid(item)}>
                          Снять отметку
                        </Button>
                      )}
                    </ActionButtonsRow>
                  </td>
                </tr>
              );
            })}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={6}>Итого по списку ({rows.length})</td>
                <td className="money">{formatMoney(totals.scheduled)}</td>
                <td className="money">{formatMoney(totals.paid)}</td>
                <td className="money">{formatMoney(totals.left)}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          )}
        </Table>
      </TableWrap>
    </Section>
  );
}
